import {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {CommentExtendedType, CommentStateEnum, CommentType} from "../types";
import {usePagination} from "./usePagination";

type CommentFetchMethod = (quiet?: boolean) => Promise<CommentType>;
type CommentCreateMethod = (submissionId: string, body: string, state?: CommentStateEnum) => Promise<CommentType>;
type CommentCreateStudioMethod = (submissionId: string, body: string) => Promise<CommentType>;
type CommentDeleteMethod = (id: string) => Promise<void>;
type CommentEditMethod = (body: string) => Promise<CommentType>;
type CommentHideMethod = (body: string) => Promise<CommentType>;
interface UseCommentReturn extends Partial<CommentType> {
  comment: CommentType | null
  fetched: boolean

  refresh: CommentFetchMethod
  createApproveComment: CommentCreateStudioMethod
  createComment: CommentCreateMethod
  createRejectComment: CommentCreateStudioMethod
  deleteComment: CommentDeleteMethod
  editComment: CommentEditMethod
  hideComment: CommentHideMethod

  // @deprecated use hideComment instead
  moderateComment: Function
  // @deprecated use body instead
  commentBody: string
  // @deprecated use id instead
  commentId: string
  // @deprecated use ownerId instead
  commentOwnerId: string
  // @deprecated use state instead
  commentState: string
}

export const useComment = (commentId?: string): UseCommentReturn => {
  const {createComment: createCommentApi, getComment, updateComment, deleteComment: deleteCommentApi} = useApi();

  const [comment, setComment] = useState<CommentType | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetch: CommentFetchMethod = async(quiet = false) => {
    try {
      if(!commentId) throw new Error("No comment to fetch");

      if(!quiet) setFetched(false);

      const comment = await withCache(getComment, [commentId], 120_000);
      setComment(comment);

      return comment;
    } catch (error) {
      console.error(`Error fetching comment ${commentId}`, error);
      throw new Error(`Error fetching comment ${commentId}`, {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const doCreateComment: CommentCreateMethod = async(submissionId, body, state) => {
    if(commentId) console.warn("It is not intended to create a comment if useComment() is specified with an id!");

    try {
      const newComment = await createCommentApi(submissionId, body, state);
      setComment(newComment);

      clearCache("getUserComments", [newComment.ownerId]);
      clearCache("getSubmissionComments", [submissionId]);

      return newComment;
    } catch (error) {
      console.error(`Error creating comment for submission ${submissionId}`, error);
      throw new Error(`Error creating comment for submission ${submissionId}`, {cause: error});
    }
  };

  const createComment: CommentCreateMethod = async(submissionId, body, state = CommentStateEnum.PUBLIC) =>
    await doCreateComment(submissionId, body, state);

  const createRejectComment: CommentCreateStudioMethod = async(submissionId, body = "") =>
    await doCreateComment(submissionId, body, CommentStateEnum.STUDIO_REJECTED);

  const createApproveComment: CommentCreateStudioMethod = async(submissionId, body = "") =>
    await doCreateComment(submissionId, body, CommentStateEnum.STUDIO_ACCEPTED);

  const deleteComment: CommentDeleteMethod = async() => {
    if(!commentId) throw new Error("No comment to delete");
    await deleteCommentApi(commentId);
  };

  const editComment: CommentEditMethod = async(body: string) => {
    if(!commentId) throw new Error("No comment to edit");
    return await updateComment(commentId, body);
  };

  const hideComment: CommentHideMethod = async() => {
    if(!commentId) throw new Error("No comment to hide");
    return await updateComment(commentId, undefined, "hidden");
  };

  useEffect(() => {
    if(commentId) fetch();
  }, [commentId]);

  return {
    comment,
    ...comment,
    commentBody: comment?.body || "",
    commentId: comment?.id || "",
    commentOwnerId: comment?.ownerId || "",
    commentState: comment?.state || "",
    createApproveComment,
    createComment,
    createRejectComment,
    deleteComment,
    editComment,
    fetched,
    hideComment,
    moderateComment: () => updateComment(comment?.id || "", undefined, "hidden"),
    refresh: fetch
  };
};

type CommentsFetchMethod = (quiet?: boolean) => Promise<CommentExtendedType[]>;
type CommentsFetchMoreMethod = (refresh?: boolean) => Promise<void>;
type CommentsDeleteMethod = (id: string) => Promise<void>;
type CommentsEditMethod = (id: string, body: string) => Promise<CommentType>;
type CommentsVisibilityMethod = (id: string) => Promise<CommentType>;

interface UseCommentsReturn {
  comments: CommentExtendedType[]
  fetched: boolean
  hasMore: boolean
  fetchMore: CommentsFetchMoreMethod
  refresh: CommentsFetchMethod
  currentPage?: number
}

const useComments = (
  scope: "submission" | "owner", paramId: string, params: {[key:string]: any} = {}
): UseCommentsReturn => {
  const {getUserComments, getSubmissionComments, updateComment, deleteComment} = useApi();

  const [comments, setComments] = useState<CommentExtendedType[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const {appendNextPage, refreshLastPage, currentPage, hasMore} =
    usePagination<CommentExtendedType>(setComments, params?.page, params?.limit);

  const extendsComments = (comments: CommentType[]): CommentExtendedType[] =>
    comments.map(comment => ({
      ...comment,
      changeVisibility: () => onChangeVisibilityComment(comment.id),
      delete: () => onDeleteComment(comment.id),
      edit: (body: string) => onEditComment(comment.id, body)
    }));


  const scopeFetch = async(cache: boolean = true, appendParams?: {[key: string]: any}) => {
    if(!scope) throw new Error("No scope defined");
    if(!["submission", "owner"].includes(scope)) throw new Error("Invalid scope");


    if(cache){
      if(scope === "owner") return await withCache(getUserComments, [paramId, {...params, ...appendParams}], 120_000);
      return await withCache(getSubmissionComments, [paramId, {...params, ...appendParams}], 60_000);
    }
    if(scope === "owner") return await getUserComments(paramId, {...params, ...appendParams});
    return await getSubmissionComments( paramId, {...params, ...appendParams});

  };

  const fetch: CommentsFetchMethod = async() => {
    try {
      setFetched(false);
      const newComments = extendsComments(await scopeFetch());

      setComments(newComments);

      return newComments;
    } catch (error) {
      console.error(`Error fetching comments for user ${paramId}`, error);
      throw new Error(`Error fetching comments for user ${paramId}`, {cause: error});
    } finally {
      setFetched(true);
    }
  };

  const fetchMore: CommentsFetchMoreMethod = async refresh => {
    try {
      setFetched(false);
      if(refresh)
        await refreshLastPage(async appendParams => extendsComments(await scopeFetch(false, appendParams)));
      else
        await appendNextPage(async appendParams => extendsComments(await scopeFetch(true, appendParams)));

    } catch (error) {
      console.error("Error fetching more comments", error);
      throw new Error("Error fetching more comments", {cause: error});
    } finally {
      setFetched(true);
    }
  };


  const onDeleteComment: CommentsDeleteMethod = async id => {
    try {
      await deleteComment(id);

      clearCache("getUserComments");
      clearCache("getSubmissionComments");

      setComments(prevComments =>
        prevComments.filter(comment => comment.id !== id)
      );
    } catch (error) {
      console.error(`Error deleting comment '${id}'`, error);
      throw new Error(`Error deleting comment '${id}'`, {cause: error});
    }
  };

  const onEditComment: CommentsEditMethod = async(id, body) => {
    try {
      const editedComment = await updateComment(id, body);

      clearCache("getUserComments");
      clearCache("getSubmissionComments");

      return editedComment;
    } catch (error) {
      console.error(`Error editing comment '${id}'`, error);
      throw new Error(`Error editing comment '${id}'`, {cause: error});
    }
  };

  const onChangeVisibilityComment: CommentsVisibilityMethod = async id => {
    try {
      const currentState = comments.find(comment => comment.id === id)?.state;
      const newState = currentState === "public" ? "hidden" : "public";

      const updatedComment = await updateComment(id, undefined, newState);

      clearCache("getSubmissionComments");
      clearCache("getUserComments");

      return updatedComment;
    } catch (error) {
      console.error(`Error updating comment visibility '${id}'`, error);
      throw new Error(`Error updating comment visibility '${id}'`, {cause: error});
    }
  };

  useEffect(() => {
    if(paramId) fetch();
  }, [scope, paramId, params?.page]);

  return {
    comments,
    currentPage,
    fetchMore,
    fetched,
    hasMore,
    refresh: fetch
  };
};

export const useCommentsByOwner = (id: string, params: {[key:string]: any}) => useComments("owner", id, params);
export const useCommentsBySubmission = (
  id: string, params: {[key:string]: any}
) => useComments("submission", id, params);
